<template>
  <section id="demo" class="intro-subsection">
    <div class="intro-content">
      <h2 class="intro-title">Découvrez toutes les fonctionnalités de Florisuite</h2>
      <p class="intro-description">
        Optimisez votre gestion florale avec une suite complète d'outils intelligents et intuitifs.
        Chaque fonctionnalité est conçue pour simplifier votre quotidien et booster votre croissance.
      </p>

      <button class="intro-button" type="button" @click="openDemo">
        <span class="intro-button-text">Voir la Démo</span>
      </button>
    </div>

    <div v-if="isOpen" class="modal-overlay" @click.self="closeDemo">
      <div class="modal" role="dialog" aria-modal="true" aria-label="Démo Florisuite">
        <button class="modal-close" type="button" @click="closeDemo" aria-label="Fermer">
          ✕
        </button>
        <div class="video-wrapper">
          <video
            ref="demoVideo"
            class="video"
            :src="demoVideoUrl"
            controls
            autoplay
            muted
            preload="metadata"
            playsinline
            title="Démo Florisuite"
            @error="handleVideoError"
          >
            Votre navigateur ne supporte pas la lecture de vidéos HTML5.
          </video>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "IntroSubsection",
  data() {
    return {
      isOpen: false,
      demoVideoUrl:
        "https://minio.ethumada.com/api/v1/buckets/ethumada-website/objects/download?preview=true&prefix=Vid%C3%A9o%20florisuite.mp4",
    };
  },
  methods: {
    openDemo() {
      this.isOpen = true;
      this.$nextTick(() => {
        const video = this.$refs.demoVideo as HTMLVideoElement | undefined;
        if (!video) return;
        video.currentTime = 0;
        void video.play().catch(() => {
          // Autoplay may still be blocked by the browser depending on policies.
        });
      });
    },
    closeDemo() {
      this.isOpen = false;
      const video = this.$refs.demoVideo as HTMLVideoElement | undefined;
      if (video) video.pause();
    },
    handleVideoError(event: Event) {
      console.error("Erreur de chargement de la vidéo:", event);
      const video = event.target as HTMLVideoElement;
      if (video) {
        console.error("Code d'erreur:", video.error?.code);
        console.error("Message:", video.error?.message);
      }
    },
  },
});
</script>

<style>
.intro-subsection {
  align-items: center;
  align-self: stretch;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 100px 30px;
  position: relative;
  width: 100%;
}

.intro-subsection .intro-content {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 27px;
  max-width: 1200px;
  min-width: 350px;
  width: 100%;
}

.intro-subsection .intro-title {
  color: #1a1a1a;
  font-family: "Montserrat", Helvetica;
  font-size: 60px;
  font-weight: 700;
  letter-spacing: -1.8px;
  line-height: 85px;
  margin: 0;
  text-align: center;
  width: 100%;
}

.intro-subsection .intro-description {
  color: #5f5f5f;
  font-family: "Montserrat", Helvetica;
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 28px;
  margin: 0;
  text-align: center;
  width: 100%;
}

.intro-subsection .intro-button {
  all: unset;
  background-color: #084734;
  border-radius: 6px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  cursor: pointer;
  height: 40px;
  max-width: 300px;
  overflow: hidden;
  position: relative;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    background-color 0.18s ease;
  width: 100%;
}

.intro-subsection .intro-button:hover {
  background-color: #0a5a42;
  box-shadow: 0px 6px 14px rgba(0, 0, 0, 0.14);
  transform: translateY(-1px);
}

.intro-subsection .intro-button:active {
  transform: translateY(0px);
}

.intro-subsection .intro-button-text {
  color: #ffffff;
  font-family: "Open Sans", Helvetica;
  font-size: 14px;
  font-weight: 600;
  left: calc(50% - 1px);
  letter-spacing: 0;
  line-height: 22px;
  position: absolute;
  text-align: center;
  top: calc(50% - 11px);
  transform: translateX(-50%);
  white-space: nowrap;
}

.intro-subsection .modal-overlay {
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 1000;
}

.intro-subsection .modal {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.35);
  max-width: 980px;
  padding: 18px;
  position: relative;
  width: 100%;
}

.intro-subsection .modal-close {
  all: unset;
  align-items: center;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  position: absolute;
  right: 10px;
  top: 10px;
  width: 36px;
}

.intro-subsection .modal-close:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.intro-subsection .video-wrapper {
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.intro-subsection .video-wrapper::before {
  content: "";
  display: block;
  padding-top: 56.25%;
}

.intro-subsection .video {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

@media (max-width: 768px) {
  .intro-subsection .intro-title {
    font-size: 42px;
    line-height: 52px;
    letter-spacing: -1px;
  }
}
</style>


